import React from "react";
import { Box, Divider, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  CheckmarkCircle02Icon,
  CancelCircleIcon,
} from "@hugeicons-pro/core-solid-standard";

/** Upload acknowledgement; receipt extraction continues in the background. */
export interface ReceiptUploadResult {
  QUEUED: { filename: string }[];
  FAILED: { filename: string }[];
}

interface PostUploadPageProps {
  uploadResult: ReceiptUploadResult;
}

const COUNT_ROW = {
  display: "flex",
  alignItems: "center",
  gap: "var(--space-3)",
} as const;

const PostUploadPage: React.FC<PostUploadPageProps> = ({ uploadResult }) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}
    >
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}
      >
        <Typography variant="h5" component="span">
          {t("Your receipts are uploaded")}
        </Typography>
        <Typography variant="body2" component="span">
          {t("ReceiptsQueuedDescription")}
        </Typography>
      </Box>
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}
      >
        <Box sx={COUNT_ROW}>
          <HugeiconsIcon
            icon={CheckmarkCircle02Icon}
            color="var(--color-text-brand)"
          />
          <Typography variant="body2" component="span">
            {uploadResult.QUEUED.length} {t("Uploaded")}
          </Typography>
        </Box>
        {uploadResult.QUEUED.map((receipt, index) => (
          <React.Fragment key={`${receipt.filename}-${index}`}>
            <Typography
              variant="body1"
              component="span"
              noWrap
              sx={{ padding: "var(--space-3)" }}
            >
              {receipt.filename}
            </Typography>
            {index !== uploadResult.QUEUED.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </Box>
      <Box sx={COUNT_ROW}>
        <HugeiconsIcon
          icon={CancelCircleIcon}
          color="var(--color-text-secondary)"
        />
        <Typography variant="body2" component="span">
          {uploadResult.FAILED.length} {t("Upload Failed")}
        </Typography>
      </Box>
    </Box>
  );
};

export default PostUploadPage;
